/** /bookings/:id/:tab? — the booking record: one booking, its guest and its party, read
 *  and edited in place (plan 2026-10-01-console-booking-order-edit-forms §1.1–§1.2, §1.6).
 *
 *  - **Page tabs** (URL): Booking (the only editable surface) · Activity · Integration ·
 *    Cache · Diagnose. The draft and the ledger are PAGE-level
 *    (the itinerary editor's lifted-draft pattern): switching tabs keeps both, the ledger
 *    stays docked on every tab while editing, and the unsaved-changes guard only fires on
 *    leaving the record (`basePath=/bookings/<id>`).
 *  - **Views** of the Booking tab (`?view=spine|tabs`, else the operator's remembered
 *    choice): the lifecycle spine, or the tabs view — one facet at a time
 *    (`?facet=`, default Booking). Both lay out the one section registry on the same
 *    draft and ledger, so switching the view or the tab keeps every unsaved change; a
 *    ledger row of a field on another tab opens that tab and focuses it.
 *  - **Edit mode** in place: `Edit` / `E` (or `?edit=1`) flips the Booking tab's
 *    FieldSlots and docks the ledger; Esc / Close editor asks first when the draft is
 *    dirty (the ledger binds ⌘Z / ⇧⌘Z / ⌘S / Esc while mounted; the page binds `E`, and
 *    `1`–`5` for the spine's stages or `1`–`7` for the tabs). No console_bookings_edit,
 *    or the kill switch off → view mode only.
 *  - A draft restored from sessionStorage — or kept after leaving edit mode — is
 *    announced in view mode too (it never shows its values there).
 *  - States: skeleton panels while loading, a slim progress line on a background refetch,
 *    standard empty states for a missing booking or one the operator may not see.
 */
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import { Alert, Box, Button } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';

import {
  BOOKING_RECORD_FACETS,
  catalogActionFields,
  useBookingEditContext,
  useBookingRecordCatalog,
  type BookingRecordCatalog,
  type BookingRecordEditContext,
  type BookingRecordFacet,
} from '@/api/bookingRecord';
import * as bookingsApi from '@/api/bookings';
import { ACTION_PREFIX, isActionPath } from '@/features/records/draft/changeSet';
import { DraftProvider, useDraft, useDraftSummary, useDraftUnsavedGuard } from '@/features/records/draft/DraftProvider';
import { useRecordShortcuts } from '@/features/records/draft/useRecordShortcuts';
import { RecordActionsProvider, useRecordActionItems } from '@/features/records/actions/RecordActionsProvider';
import { RecordEditorProvider, useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { Ledger } from '@/features/records/ledger/Ledger';
import { LedgerDock, SHEET_BELOW_PX } from '@/features/records/ledger/LedgerDock';
import {
  APPBAR_PX,
  DOCK_GAP_PX,
  missingStatus,
  prefersReducedMotion,
  RecordPageTabs,
  retryUnlessMissing,
  SCROLLER_PADDING_PX,
  useElementWidth,
  useRecordNav,
  type RecordNav as RecordPageNav,
} from '@/features/records/page/recordPage';
import type { RecordActionGroup } from '@/features/records/page/RecordActionsMenu';
import { RecordSkeleton } from '@/features/records/page/RecordStates';
import { usePermissions } from '@/hooks/usePermissions';
import { useUserPreference } from '@/hooks/useUserPreferences';
import { formatZoned } from '@/utils/zonedTime';

import { AiDiagnosePanel } from './diagnose/AiDiagnosePanel';
import { userName } from './overview/common';
import { useBookingRecordEditorConfig } from './record/editorConfig';
import { RecordHeader, type BookingRecordView } from './record/RecordHeader';
import { BookingNoPermission, BookingNotFound } from './record/RecordStates';
import { BOOKING_RECORD_REGISTRY, FACET_TITLES, SPINE_STAGES, type SpineStage } from './record/registry';
import { TimelineNavContext, type TimelineNav } from './record/panels/TimeAxis';
import { ActivityTab, type ActivityIntent } from './record/tabs/ActivityTab';
import { CacheTab } from './record/tabs/CacheTab';
import { IntegrationTab, type IntegrationIntent } from './record/tabs/IntegrationTab';
import { useBookingImmediateActions } from './record/useBookingImmediateActions';
import { FacetTabs } from './record/views/FacetTabs';
import { BOOKING_RECORD_VIEWS } from './record/views/recordViews';
import { managedByName, stageAnchorId, type BookingRecordData } from './record/views/spineLayout';
import { spineModel } from './record/views/spineModel';
import { SpineNav } from './record/views/SpineNav';
import { SpineView } from './record/views/SpineView';
import { TabsView } from './record/views/TabsView';
import { useSectionMarks, useSpineMarks } from './record/views/useSectionMarks';

export type BookingTabId = 'booking' | 'activity' | 'integration' | 'cache' | 'diagnose';

const TABS: { id: BookingTabId; label: string; perm?: string }[] = [
  { id: 'booking', label: 'Booking' },
  { id: 'activity', label: 'Activity' },
  { id: 'integration', label: 'Integration' },
  { id: 'cache', label: 'Cache' },
  { id: 'diagnose', label: 'Diagnose', perm: 'console_entity_ai' },
];
const TABS_LABELS = { label: 'Booking sections', refreshingLabel: 'Refreshing the booking' };

const VIEW_PREFERENCE = 'record_view:booking';

function selectableView(v: unknown): BookingRecordView | null {
  const def = BOOKING_RECORD_VIEWS.find((x) => x.id === v);
  return def && !def.disabled ? (def.id as BookingRecordView) : null;
}

const FACET_TABS = BOOKING_RECORD_FACETS.map((id) => ({ id, label: FACET_TITLES[id] }));
const DEFAULT_FACET: BookingRecordFacet = 'booking';

/** The `?facet=` value for a tab (the default one has none). */
const facetParam = (facet: BookingRecordFacet) => (facet === DEFAULT_FACET ? null : facet);

/** The tabs-view tab that holds the field a draft path changes. */
function facetOfPath(catalog: BookingRecordCatalog, path: string): BookingRecordFacet | undefined {
  const key = isActionPath(path) ? catalogActionFields(catalog)[path.slice(ACTION_PREFIX.length)] : path;
  return key ? BOOKING_RECORD_REGISTRY[key as keyof typeof BOOKING_RECORD_REGISTRY]?.facet : undefined;
}

type RecordNav = RecordPageNav<BookingTabId>;

export function BookingDetailPage() {
  const { id: idStr, tab } = useParams<{ id: string; tab?: string }>();
  const id = Number(idStr);
  if (!Number.isInteger(id) || id <= 0) return <BookingNotFound id={idStr ?? ''} />;
  return <BookingRecordPage key={id} id={id} tabParam={tab} />;
}

function BookingRecordPage({ id, tabParam }: { id: number; tabParam: string | undefined }) {
  const { hasPerm } = usePermissions();
  const nav = useRecordNav<BookingTabId>(`/bookings/${id}`, 'booking');
  const [params] = useSearchParams();

  const detailQ = useQuery({
    queryKey: bookingsApi.bookingDetailKey(id),
    queryFn: () => bookingsApi.retrieve(id),
    retry: retryUnlessMissing,
  });
  const missing = missingStatus(detailQ.error);
  const catalogQ = useBookingRecordCatalog();
  const ctxQ = useBookingEditContext(id, { enabled: missing === null });

  const tabs = TABS.filter((t) => !t.perm || hasPerm(t.perm));
  const activeTab: BookingTabId = tabs.find((t) => t.id === tabParam)?.id ?? 'booking';
  const section = tabs.find((t) => t.id === activeTab)?.label ?? 'Booking';

  // `?view=` wins, then the operator's remembered view, then the spine.
  const pref = useUserPreference<string>(VIEW_PREFERENCE);
  const view: BookingRecordView =
    [params.get('view'), pref.value].map(selectableView).find((v) => v !== null) ?? 'spine';
  const facet = BOOKING_RECORD_FACETS.find((f) => f === params.get('facet')) ?? DEFAULT_FACET;
  const { setParam } = nav;
  const { value: remembered, setValue: remember } = pref;
  const onView = useCallback(
    (v: BookingRecordView) => {
      setParam('view', v);
      if (remembered !== v) remember(v);
    },
    [setParam, remembered, remember],
  );

  if (missing === 404) return <BookingNotFound id={id} />;
  if (missing === 403) return <BookingNoPermission id={id} />;

  const detail = detailQ.data;
  const catalog = catalogQ.data;
  const editContext = ctxQ.data;
  if (!detail || !catalog || !editContext) {
    const failed = detailQ.error ?? catalogQ.error ?? ctxQ.error;
    return (
      <Box>
        <RecordHeader
          id={id}
          detail={detail}
          editContext={editContext}
          section={section}
          canEdit={false}
          editing={false}
          editingDisabled={false}
          onEdit={() => undefined}
          onClose={() => undefined}
          view={view}
          onView={onView}
          integrationHref={nav.hrefFor('integration')}
        />
        <RecordPageTabs tabs={tabs} active={activeTab} hrefFor={nav.hrefFor} fetching={false} {...TABS_LABELS} />
        {failed ? (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  void detailQ.refetch();
                  void catalogQ.refetch();
                  void ctxQ.refetch();
                }}
              >
                Retry
              </Button>
            }
          >
            Couldn’t load booking #{id}
            {failed instanceof Error && failed.message ? ` — ${failed.message}` : ''}.
          </Alert>
        ) : (
          <RecordSkeleton label="Loading the booking" />
        )}
      </Box>
    );
  }

  const canEdit =
    hasPerm('console_bookings_edit') &&
    editContext.permissions.console_bookings_edit &&
    catalog.enabled &&
    editContext.flags.booking_edit_enabled;
  const editingDisabled = !(catalog.enabled && editContext.flags.booking_edit_enabled);

  return (
    <BookingRecordLoaded
      id={id}
      tabs={tabs}
      activeTab={activeTab}
      section={section}
      detail={detail}
      catalog={catalog}
      editContext={editContext}
      canEdit={canEdit}
      editing={canEdit && params.get('edit') === '1'}
      editingDisabled={editingDisabled}
      fetching={detailQ.isFetching || ctxQ.isFetching}
      nav={nav}
      view={view}
      onView={onView}
      facet={facet}
    />
  );
}

interface LoadedProps {
  id: number;
  tabs: { id: BookingTabId; label: string }[];
  activeTab: BookingTabId;
  section: string;
  detail: bookingsApi.BookingDetail;
  catalog: BookingRecordCatalog;
  editContext: BookingRecordEditContext;
  canEdit: boolean;
  editing: boolean;
  editingDisabled: boolean;
  fetching: boolean;
  nav: RecordNav;
  view: BookingRecordView;
  onView: (v: BookingRecordView) => void;
  /** The tabs view's open tab. */
  facet: BookingRecordFacet;
}

interface PendingFocus {
  rec: string;
  path: string;
}

function BookingRecordLoaded(props: LoadedProps) {
  const { id, activeTab, detail, catalog, editContext, editing, canEdit, nav, view, facet } = props;
  // A ledger row for a field that is not on screen (another page tab, or another tab of
  // the tabs view): open the Booking tab on the field's tab, then focus the field once
  // its FieldSlot has mounted.
  const pendingFocus = useRef<PendingFocus | null>(null);
  const shown = useRef({ activeTab, view, facet });
  shown.current = { activeTab, view, facet };
  const { goTab, setParam } = nav;
  const onFocusMissing = useCallback(
    (rec: string, path: string) => {
      const now = shown.current;
      const target = now.view === 'tabs' ? facetOfPath(catalog, path) : undefined;
      const otherFacet = target !== undefined && target !== now.facet;
      if (now.activeTab === 'booking' && !otherFacet) return;
      pendingFocus.current = { rec, path };
      goTab('booking', otherFacet ? { facet: facetParam(target) } : undefined);
    },
    [goTab, catalog],
  );
  const config = useBookingRecordEditorConfig({ catalog, editContext, detail, onFocusMissing });
  const onExitEdit = useCallback(() => setParam('edit', null), [setParam]);
  const onEnterEdit = useCallback(() => setParam('edit', '1'), [setParam]);

  return (
    <DraftProvider
      kind="booking"
      recordId={id}
      baselines={editContext.baselines}
      baselineTs={editContext.generated_ts}
      actionFields={catalogActionFields(catalog)}
    >
      <RecordEditorProvider mode={editing ? 'edit' : 'view'} config={config} onExitEdit={onExitEdit}>
        <RecordActionsProvider enabled={canEdit} onEnterEdit={onEnterEdit}>
          <RecordShell {...props} pendingFocus={pendingFocus} />
        </RecordActionsProvider>
      </RecordEditorProvider>
    </DraftProvider>
  );
}

function RecordShell({
  id,
  tabs,
  activeTab,
  section,
  detail,
  catalog,
  editContext,
  canEdit,
  editing,
  editingDisabled,
  fetching,
  nav,
  view,
  onView,
  facet,
  pendingFocus,
}: LoadedProps & { pendingFocus: MutableRefObject<PendingFocus | null> }) {
  const editor = useRecordEditor();
  const { restored, discardRestored, dispatch } = useDraft();
  const { count } = useDraftSummary();
  const { prompt } = useDraftUnsavedGuard(`/bookings/${id}`);
  const marks = useSpineMarks();
  const facetMarks = useSectionMarks('facet');
  const data = useMemo<BookingRecordData>(() => ({ catalog, editContext, detail }), [catalog, editContext, detail]);
  const model = useMemo(() => spineModel({ detail, editContext }), [detail, editContext]);
  const { setParam, goTab } = nav;
  const enterEdit = useCallback(() => setParam('edit', '1'), [setParam]);

  // The Actions menu's "What the feed sent" / "View raw … data" land on a section of the
  // Integration tab (in memory: a link must never trigger an audited vendor read).
  const [integrationIntent, setIntegrationIntent] = useState<IntegrationIntent | null>(null);
  const onIntegration = useCallback(
    (section: 'feed' | 'raw', fetch = false) => {
      setIntegrationIntent((prev) => ({ section, fetch, seq: (prev?.seq ?? 0) + 1 }));
      goTab('integration');
    },
    [goTab],
  );
  const clearIntent = useCallback(() => setIntegrationIntent(null), []);
  // The time axis's dots land on their row of the Activity or Integration tab.
  const [activityIntent, setActivityIntent] = useState<ActivityIntent | null>(null);
  const clearActivityIntent = useCallback(() => setActivityIntent(null), []);
  const timelineNav = useMemo<TimelineNav>(
    () => ({
      onShow: (target) => {
        if (target.tab === 'integration') {
          setIntegrationIntent((prev) => ({ section: 'imports', highlight: target.highlight, seq: (prev?.seq ?? 0) + 1 }));
          goTab('integration');
        } else {
          setActivityIntent((prev) => ({ section: target.section, highlight: target.highlight, seq: (prev?.seq ?? 0) + 1 }));
          goTab('activity');
        }
      },
    }),
    [goTab],
  );
  const immediate = useBookingImmediateActions({
    bookingId: id,
    editContext,
    onTab: goTab,
    onIntegration,
    guestName: (detail.guest && userName(detail.guest)) || 'the guest',
  });
  const recordItems = useRecordActionItems('booking', id);
  const actionGroups: RecordActionGroup[] = [{ key: 'record', label: 'Record', items: recordItems }, ...immediate.groups];

  // The spine's (or the tab bar's) height places the ledger's side column under it and
  // clears it when a node scrolls a panel into view.
  const spineRef = useRef<HTMLDivElement | null>(null);
  const [spineHeight, setSpineHeight] = useState(0);
  const onBooking = activeTab === 'booking';
  const tabsView = view === 'tabs';
  useLayoutEffect(() => {
    const el = spineRef.current;
    if (!el) {
      setSpineHeight(0);
      return undefined;
    }
    const measure = () => setSpineHeight(el.getBoundingClientRect().height);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [onBooking, tabsView]);
  const [contentRef, contentWidth] = useElementWidth<HTMLDivElement>();

  const focusStage = useCallback((stage: SpineStage) => {
    const panel = document.getElementById(stageAnchorId(stage));
    if (!panel) return;
    panel.scrollIntoView?.({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    panel.focus({ preventScroll: true });
  }, []);

  // A tab switch brings the tab panel's top back under the sticky tab bar when the page
  // was scrolled past it; `1`–`7` also move focus into it (a click keeps it on the tab).
  const tabPanelRef = useRef<HTMLDivElement | null>(null);
  const selectFacet = useCallback(
    (next: BookingRecordFacet, focus = false) => {
      const panel = tabPanelRef.current;
      const bar = spineRef.current;
      if (panel && bar && panel.getBoundingClientRect().top < bar.getBoundingClientRect().bottom) {
        panel.scrollIntoView?.({ block: 'start' });
      }
      if (focus) panel?.focus({ preventScroll: true });
      setParam('facet', facetParam(next));
    },
    [setParam],
  );

  useRecordShortcuts(
    {
      ...(canEdit && !editing ? { onEdit: enterEdit } : {}),
      ...(onBooking
        ? {
            onStage: (i: number) => {
              if (tabsView) {
                const next = BOOKING_RECORD_FACETS[i];
                if (next) selectFacet(next, true);
                return;
              }
              const stage = SPINE_STAGES[i];
              if (stage) focusStage(stage);
            },
          }
        : {}),
    },
    { stageCount: tabsView ? BOOKING_RECORD_FACETS.length : SPINE_STAGES.length },
  );

  useEffect(() => {
    if (!onBooking || !pendingFocus.current) return undefined;
    // Taken when the frame runs, not before: an editor update in between (a preview
    // arriving) re-runs this effect, whose cleanup cancels the frame — the pending focus
    // must survive that to be tried again.
    const raf = requestAnimationFrame(() => {
      const pending = pendingFocus.current;
      pendingFocus.current = null;
      if (pending) editor.focusField(pending.rec, pending.path);
    });
    return () => cancelAnimationFrame(raf);
  }, [onBooking, facet, editor, pendingFocus]);

  const tz = editContext.schedule.timezone;
  const stickyTop = (onBooking ? spineHeight : 0) - SCROLLER_PADDING_PX + DOCK_GAP_PX;

  return (
    <Box sx={{ '--record-spine-height': `${spineHeight + DOCK_GAP_PX}px` }} data-record-page={id}>
      <RecordHeader
        id={id}
        detail={detail}
        editContext={editContext}
        section={section}
        canEdit={canEdit}
        editing={editing}
        editingDisabled={editingDisabled}
        onEdit={enterEdit}
        onClose={editor.requestClose}
        view={view}
        onView={onView}
        integrationHref={nav.hrefFor('integration')}
        actionGroups={actionGroups}
      />

      {!editing && count > 0 && (
        <Alert
          severity="info"
          icon={<EditNoteOutlinedIcon />}
          sx={{ mb: 2 }}
          data-view-draft-banner
          action={
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {canEdit && (
                <Button color="inherit" size="small" onClick={enterEdit}>
                  Continue editing
                </Button>
              )}
              <Button
                color="inherit"
                size="small"
                onClick={restored ? discardRestored : () => dispatch({ type: 'DISCARD_ALL' })}
              >
                Discard
              </Button>
            </Box>
          }
        >
          {restored
            ? `Restored ${count} unsaved ${count === 1 ? 'change' : 'changes'} from ${formatZoned(restored.savedAt, tz, { weekday: false })} — not saved yet.`
            : `${count} unsaved ${count === 1 ? 'change is' : 'changes are'} kept in the editor — not saved yet.`}
        </Alert>
      )}

      <RecordPageTabs tabs={tabs} active={activeTab} hrefFor={nav.hrefFor} fetching={fetching} {...TABS_LABELS} />

      {onBooking &&
        (tabsView ? (
          <FacetTabs
            ref={spineRef}
            facets={FACET_TABS}
            active={facet}
            editing={editing}
            marks={facetMarks}
            managedBy={managedByName(editContext)}
            onSelect={selectFacet}
          />
        ) : (
          <SpineNav
            ref={spineRef}
            model={model}
            editing={editing}
            marks={marks}
            managedBy={managedByName(editContext)}
            onSelect={focusStage}
            compactMarks={contentWidth > 0 && contentWidth < SHEET_BELOW_PX}
          />
        ))}

      <Box ref={contentRef}>
        <LedgerDock
          open={editing}
          stickyTop={stickyTop}
          viewportOffset={APPBAR_PX + SCROLLER_PADDING_PX}
          ledger={(variant) => <Ledger variant={variant} />}
        >
          {activeTab === 'booking' && (
            <TimelineNavContext.Provider value={timelineNav}>
              {tabsView ? (
                <TabsView ref={tabPanelRef} data={data} width={contentWidth} facet={facet} pageTabHref={nav.hrefFor} />
              ) : (
                <SpineView data={data} width={contentWidth} />
              )}
            </TimelineNavContext.Provider>
          )}
          {activeTab === 'activity' && (
            <ActivityTab
              bookingId={id}
              detail={detail}
              editContext={editContext}
              intent={activityIntent}
              onIntentDone={clearActivityIntent}
            />
          )}
          {activeTab === 'integration' && (
            <IntegrationTab bookingId={id} editContext={editContext} intent={integrationIntent} onIntentDone={clearIntent} />
          )}
          {activeTab === 'cache' && <CacheTab bookingId={id} editContext={editContext} cache={immediate.cache} />}
          {activeTab === 'diagnose' && <AiDiagnosePanel bookingId={id} tenantId={detail.tenant?.id} />}
        </LedgerDock>
      </Box>

      <Box sx={{ mt: 4, color: 'text.secondary', fontSize: 13 }}>
        <RouterLink to="/bookings" style={{ color: '#0a1a3f' }}>
          ← Back to bookings
        </RouterLink>
      </Box>
      {immediate.host}
      {prompt}
    </Box>
  );
}
